<script setup lang="ts">
import {computed, nextTick, onBeforeUnmount, ref, watch} from 'vue';
import LoginFormField from '@/features/auth/LoginFormField.vue';
import {useIdentShortPoll} from '@/features/auth/composables/useIdentShortPoll';
import type {
    SmartIdCeremonyApi,
    SmartIdCeremonyHandle,
    SmartIdCeremonyInitiateResponse,
    SmartIdCeremonySuccess
} from '@/features/auth-ceremony/types';

defineOptions({name: 'SmartIdCeremony'});

const props = withDefaults(defineProps<{
    translations: Record<string, string>;
    apiConfig: SmartIdCeremonyApi;
    /** When provided, the identity input is hidden and this value is used as the personal code. */
    rememberedIdentityNumber?: string | null;
    /** Data-test id prefix used by the e2e suite. Login uses "sid", binding uses "bind-sid". */
    testIdPrefix?: string;
}>(), {
    rememberedIdentityNumber: null,
    testIdPrefix: 'sid'
});

const identityNumber = defineModel<string>('identityNumber', {default: ''});

const emit = defineEmits<{
    success: [payload: SmartIdCeremonySuccess];
    errorCode: [code: string | null];
}>();

const shortPoll = useIdentShortPoll();

const verificationCode = ref<string | null>(null);
const identityError = ref('');
const isInitiating = ref(false);

const identityInput = ref<HTMLInputElement | null>(null);

watch(identityNumber, () => { identityError.value = ''; });

// Auto-mirror the remembered identity into the bound field so submit logic
// (and the existing API contract) keeps working unchanged. When the user
// switches to an unremembered account, clear the stale prefill so the now-
// visible field doesn't display the previous user's value.
watch(() => props.rememberedIdentityNumber, (id, prev) => {
    if (id) {
        identityNumber.value = id;
    } else if (prev) {
        identityNumber.value = '';
    }
}, {immediate: true});

const showIdentityField = computed(() => !props.rememberedIdentityNumber);

function t(key: string, fallback?: string): string {
    const value = props.translations[key];
    if (value && value !== key.toLowerCase()) return value;
    return fallback ?? key;
}

const errorMessage = computed(() => {
    const code = shortPoll.errorCode.value;
    if (!code) return '';
    if (code === 'NetworkError') return t('auth-interror');
    if (code === 'Aborted') return '';
    if (code === 'TooManyRequests') return t('lbl-too-many-login-requests');
    // Refusal is a deliberate user action — hide the message so the form
    // re-renders silently, matching the Mobile-ID cancel-codes path above.
    if (code === 'UserRefusedException'
        || code === 'UserRefusedConfirmationMessageException'
        || code === 'UserRefusedConfirmationMessageWithVerificationChoiceException'
        || code === 'UserRefusedDisplayTextAndPinException'
        || code === 'UserRefusedCertChoiceException'
        || code === 'UserRefusedVerificationChoiceException') {
        return '';
    }
    if (code === 'UserSelectedWrongVerificationCodeException') return t('auth-interror');
    if (code === 'TIMEOUT' || code === 'SessionTimeoutException') return t('auth-smartid-sessiontimeout');
    if (code === 'UserAccountNotFoundException' || code === 'NoSuitableAccountOfRequestedTypeFoundException') {
        return t('auth-smartid-usernotfound');
    }
    if (code === 'DocumentUnusableException') return t('auth-smartid-docunusable');
    if (code === 'MissingParameters') return t('lbl-field-required');
    if (code === 'AuthIdentityNotLinked') return t('lbl-auth-identity-not-linked');
    return t('auth-smartid-error-occurred');
});

const isWaiting = computed(() => shortPoll.phase.value === 'waiting');
const isBusyState = computed(() => isInitiating.value || isWaiting.value || !!verificationCode.value);

async function validate(): Promise<boolean> {
    identityError.value = showIdentityField.value && !identityNumber.value.trim() ? t('lbl-field-required') : '';
    if (identityError.value) {
        shortPoll.errorCode.value = null;
        await nextTick();
        identityInput.value?.focus();
        return false;
    }
    return true;
}

async function start(): Promise<void> {
    if (!(await validate())) return;

    isInitiating.value = true;
    try {
        const result = await props.apiConfig.initiate({
            identityNumber: identityNumber.value.trim()
        });

        if (result.retryAfterSeconds != null) {
            shortPoll.errorCode.value = 'TooManyRequests';
            emit('errorCode', 'TooManyRequests');
            return;
        }

        if (result.networkError) {
            shortPoll.errorCode.value = 'NetworkError';
            emit('errorCode', 'NetworkError');
            return;
        }

        if (!result.ok || !result.data) {
            shortPoll.errorCode.value = result.errorCode ?? null;
            emit('errorCode', result.errorCode ?? null);
            return;
        }

        verificationCode.value = result.data.verificationCode ?? null;
        await pollForCompletion(result.data);
    } finally {
        isInitiating.value = false;
    }
}

async function pollForCompletion(initiateData: SmartIdCeremonyInitiateResponse) {
    const {url, init} = props.apiConfig.completeRequest({
        identityNumber: identityNumber.value.trim(),
        ...initiateData
    });
    const outcome = await shortPoll.run(url, init);

    if (outcome.aborted) {
        verificationCode.value = null;
        return;
    }

    if (outcome.data) {
        emit('success', {
            identityNumber: identityNumber.value,
            completion: outcome.data
        });
        return;
    }

    // Any non-success outcome (including SK errors like RelyingPartyAccountConfigurationException
    // and PersonalCodeNotLinked) terminates the ceremony — the session is one-shot. Bounce the user
    // back to the form so they can retry; the error message stays visible via shortPoll.errorCode.
    verificationCode.value = null;
}

function cancel() {
    shortPoll.abort();
    verificationCode.value = null;
}

onBeforeUnmount(() => {
    shortPoll.abort();
});

defineExpose<SmartIdCeremonyHandle>({
    start,
    cancel,
    validate,
    isBusy: () => isBusyState.value
});
</script>

<template>
    <div class="smart-id-ceremony">
        <template v-if="!isWaiting && !verificationCode">
            <LoginFormField
                v-if="showIdentityField"
                :label="t('lbl-personalcode')"
                :html-for="`${testIdPrefix}-identity`"
                :error="identityError"
            >
                <input
                    :id="`${testIdPrefix}-identity`"
                    ref="identityInput"
                    v-model="identityNumber"
                    type="text"
                    autocomplete="off"
                    class="identity-input"
                    :class="{'input--invalid': identityError}"
                    :disabled="isInitiating"
                    :data-test="`${testIdPrefix}-identity`"
                />
            </LoginFormField>
        </template>

        <template v-else>
            <div class="verification-prompt" :data-test="`${testIdPrefix}-verification`">
                <div class="verification-prompt__caption">
                    {{ t('lbl-ident-smartid-code-message') }}
                </div>
                <div class="verification-prompt__code" :data-test="`${testIdPrefix}-verification-code`">
                    {{ verificationCode }}
                </div>
                <div class="verification-prompt__waiting">
                    {{ t('lbl-ident-waiting-smartid-connection') }}
                </div>
            </div>
        </template>

        <div v-if="errorMessage" class="error-div" :data-test="`${testIdPrefix}-error`">{{ errorMessage }}</div>

        <button
            v-if="isWaiting || verificationCode"
            type="button"
            class="cancel-button btn"
            :disabled="!isWaiting"
            :data-test="`${testIdPrefix}-cancel`"
            @click="cancel"
        >
            {{ t('btn-cancel') }}
        </button>
    </div>
</template>

<style scoped>
.smart-id-ceremony {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.identity-input {
    height: 36px;
    padding: 0 12px;
    margin: 0;
    border: 1px solid #ddd;
    border-radius: 1px;
    font-size: 14px;
    background: #fff;
    box-sizing: border-box;
}

.identity-input:focus {
    outline: none;
    border-color: #2eb336;
}

/* Bottom-only 2px red underline; box-sizing keeps the 36px outer box. */
.identity-input.input--invalid {
    border-bottom-color: #c1272d;
    border-bottom-width: 2px;
}

.error-div {
    color: #c1272d;
    min-height: 20px;
}

.verification-prompt {
    display: flex;
    flex-direction: column;
    gap: 8px;
    text-align: center;
}

.verification-prompt__caption {
    font-weight: 600;
}

.verification-prompt__code {
    font-size: 32px;
    font-weight: 700;
    letter-spacing: 4px;
}
</style>
